import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import './Navbar.css';

// SVG Heart Logo component
const HeartLogo = () => (
  <div className="heart-logo" style={{
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    background: '#DC2626',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 8px rgba(220, 38, 38, 0.4)'
  }}>
    <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
    </svg>
  </div>
);

const Navbar = () => {
  const [showMore, setShowMore] = useState(false);
  const [visibleItems, setVisibleItems] = useState([]);
  const [hasAnimated, setHasAnimated] = useState(false);
  const location = useLocation();
  const navRef = useRef(null);

  // Animazione entrata sequenziale (solo prima volta)
  useEffect(() => {
    const alreadyAnimated = sessionStorage.getItem('navbar-animated');

    if (!alreadyAnimated) {
      // Animazione sequenziale da sinistra a destra
      const totalItems = 6; // 5 menu items + pulsante Altro
      for (let i = 0; i < totalItems; i++) {
        setTimeout(() => {
          setVisibleItems(prev => [...prev, i]);
        }, i * 120); // 120ms tra ogni elemento
      }

      setTimeout(() => {
        setHasAnimated(true);
        sessionStorage.setItem('navbar-animated', 'true');
      }, totalItems * 120 + 600);
    } else {
      // Se già animato, mostra tutto subito
      setVisibleItems([0, 1, 2, 3, 4, 5]);
      setHasAnimated(true);
    }
  }, []);

  // Chiudi menu quando cambia route
  useEffect(() => {
    setShowMore(false);
  }, [location]);

  // Menu items - ORDINE: Cuore → Dashboard → Ricordi → Semantic → AllenAI
  const mainMenuItems = [
    { to: '/voice-e-cuore', label: 'Cuore', color: 'heart', isHeart: true },
    { to: '/', label: 'Dashboard', color: 'green', end: true },
    { to: '/ricordi', label: 'Ricordi', color: 'coral' },
    { to: '/semantic', label: 'Semantic', color: 'yellow' },
    { to: '/training', label: 'AllenAI', color: 'blue' }
  ];

  // Menu "Altro": Monitor, Voci, Demo, App
  const moreMenuItems = [
    { to: '/monitor', label: 'Monitor', color: 'orange' },
    { to: '/voices', label: 'Voci', color: 'teal' },
    { to: '/voice-demo', label: 'Demo', color: 'pink' },
    { type: 'external', label: 'Startup', color: 'gold', url: 'https://semanticai-startup.vercel.app' }
  ];

  // Check if any item in "Altro" is active
  const isMoreActive = moreMenuItems.some(item =>
    item.to && (item.end ? location.pathname === item.to : location.pathname.startsWith(item.to))
  );

  // Funzione per aprire link esterno
  const handleExternalLink = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    setShowMore(false);
  };

  // Ottieni classe animazione per item
  const getItemClass = (index) => {
    if (hasAnimated || visibleItems.includes(index)) {
      return 'nav-visible';
    }
    return 'nav-hidden';
  };

  return (
    <>
      {/* OVERLAY */}
      {showMore && (
        <div
          className="navbar-overlay"
          onClick={() => setShowMore(false)}
        />
      )}

      {/* NAVBAR PRINCIPALE */}
      <nav className="navbar-bottom" ref={navRef}>
        {/* MENU PRINCIPALE (sempre visibili) */}
        {mainMenuItems.map((item, index) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={`nav-link ${getItemClass(index)}`}
            end={item.end}
            style={{ animationDelay: `${index * 0.12}s` }}
          >
            {item.isHeart ? (
              <HeartLogo />
            ) : (
              <span className={`nav-dot ${item.color}`}></span>
            )}
            <span>{item.label}</span>
          </NavLink>
        ))}

        {/* PULSANTE "ALTRO" */}
        <button
          className={`nav-link more-btn ${isMoreActive ? 'active' : ''} ${getItemClass(5)}`}
          onClick={() => setShowMore(!showMore)}
          style={{ animationDelay: '0.6s' }}
        >
          <div className="nav-dot-more">
            <span className="nav-dot orange"></span>
            <span className="nav-dot gold"></span>
          </div>
          <span>Altro</span>
        </button>

        {/* MENU DESKTOP (solo ≥768px) */}
        {moreMenuItems.map((item, index) => (
          item.type === 'external' ? (
            <button
              key={item.label}
              className={`nav-link desktop-only app-btn ${getItemClass(6 + index)}`}
              onClick={() => handleExternalLink(item.url)}
              style={{ animationDelay: `${(6 + index) * 0.12}s` }}
            >
              <span className={`nav-dot ${item.color}`}></span>
              <span>{item.label}</span>
            </button>
          ) : (
            <NavLink
              key={item.to}
              to={item.to}
              className={`nav-link desktop-only ${getItemClass(6 + index)}`}
              end={item.end}
              style={{ animationDelay: `${(6 + index) * 0.12}s` }}
            >
              <span className={`nav-dot ${item.color}`}></span>
              <span>{item.label}</span>
            </NavLink>
          )
        ))}
      </nav>

      {/* MENU ESPANSO (slide-up mobile) */}
      {showMore && (
        <div className="navbar-expanded">
          <div className="expanded-header">
            <h3>Menu</h3>
            <button
              className="close-btn"
              onClick={() => setShowMore(false)}
            >
              ✕
            </button>
          </div>
          <div className="expanded-menu">
            {moreMenuItems.map((item) => (
              item.type === 'external' ? (
                <button
                  key={item.label}
                  className="expanded-link app-assistito-expanded"
                  onClick={() => handleExternalLink(item.url)}
                >
                  <span className={`nav-dot ${item.color}`}></span>
                  <span>{item.label}</span>
                </button>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className="expanded-link"
                  end={item.end}
                  onClick={() => setShowMore(false)}
                >
                  <span className={`nav-dot ${item.color}`}></span>
                  <span>{item.label}</span>
                </NavLink>
              )
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
